import i18n from "@/i18n";
import { askConfirm, showNotice } from "@/state/confirm-store";

type ConfirmOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

/**
 * Confirmation dialog — the app's own `<ConfirmDialog />`, on every platform.
 *
 * This module exists because React Native's `Alert.alert` is not merely
 * degraded on web — `Alert` there is `static alert() {}`, an empty function —
 * so every confirm flow that reached for it directly was silently discarded,
 * buttons and callbacks alike. Web moved to the in-app dialog first (replacing
 * `window.confirm`, which wore the browser's chrome: "localhost:8081 says",
 * OS buttons, no theme).
 *
 * Native followed by owner decision (2026-08-21): the system alert was the
 * platform-correct affordance, but it is also the one surface in the app the
 * theme cannot reach — an OS-styled interruption over an app that styles
 * everything else. One dialog, one look, every platform; and this module no
 * longer touches `Alert` at all, which `web-dialogs.test.ts` now holds for
 * the whole tree.
 *
 * The trade accepted with it: the system alert queued concurrent prompts,
 * the in-app dialog refuses them (`confirm-store`'s no-stacking rule settles
 * the second ask as "not confirmed"). That was already true on web and is the
 * safer answer for anything destructive.
 *
 * Every path out of the dialog that is not a deliberate press of the confirm
 * button resolves `false` — backdrop, hardware back, cancel.
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  const {
    title,
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive,
  } = options;

  return askConfirm({
    title,
    message,
    confirmLabel,
    cancelLabel,
    destructive: Boolean(destructive),
  });
}

/**
 * Informational alert, through the same in-app dialog, with a single button —
 * a notice has nothing to decline.
 *
 * Still fire-and-forget, so no call site changes. If a dialog is already open
 * the notice is dropped by the store's no-stacking rule — acceptable because
 * the user is, in that case, already reading an interruption rather than
 * looking at a tap that did nothing.
 */
export function notify(title: string, message?: string): void {
  showNotice(title, message, i18n.t("common.okay"));
}
